import { Button } from '@pancakeswap/uikit'
import Link from 'next/link'
import { useEffect, useId, useRef } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { AlertsState, linkAlerts, toggleAlerts, useAlerts } from './alertsStore'

/**
 * The alerts switch (Bez, 2026-10-07): one on/off control, the same state in Settings and on the profile (not in More, same day).
 * On = public alerts at once (no signature); with an existing claimed-profile session the same tap also turns on personal alerts.
 * Type: 16px title, 14px lines (never under 12px); the switch is 52 x 32 with a 44px+ hit area.
 */
export const CLAIM_LINE = 'For personal alerts, claim your profile (one free signature)'
export const CONFIRM_LINE = 'For personal alerts, confirm it’s you (one free signature)'
export const CLAIM_HREF = '/u'

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`
const GRADIENT = 'linear-gradient(135deg, #7fd4e8, #9a7bff)'

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`

const SwitchButton = styled.button<{ $on: boolean; $busy: boolean }>`
  appearance: none;
  -webkit-appearance: none;
  flex: none;
  position: relative;
  width: 52px;
  height: 32px;
  margin: 0;
  padding: 0;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${({ $on, theme }) => ($on ? 'transparent' : theme.isDark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.18)')};
  background: ${({ $on, theme }) => ($on ? GRADIENT : theme.isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)')};
  transition: background 180ms;
  &::before {
    /* a 48px+ hit area around the 52 x 32 switch */
    content: '';
    position: absolute;
    inset: -8px;
  }
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    transform: translateX(${({ $on }) => ($on ? '20px' : '0')});
    transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
    ${({ $busy }) =>
      $busy &&
      css`
        animation: ${pulse} 1s ease-in-out infinite;
      `}
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &::after {
      transition: none;
      animation: none;
    }
  }
`

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
`
const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
`
const Status = styled.p`
  margin: 2px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Detail = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const ClaimLink = styled.a`
  display: inline-block;
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;
  color: #7fd4e8;
  text-decoration: underline;
  text-underline-offset: 3px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 2px;
  }
`

function statusLine(s: AlertsState): string {
  if (s.busy) return s.busy === 'on' ? 'Turning on…' : 'Turning off…'
  switch (s.kind) {
    case 'loading':
      return 'Checking this device…'
    case 'unsupported':
      return 'Not available in this browser'
    case 'ios-install-first':
      return 'iPhone and iPad: only in the installed app'
    case 'ios-too-old':
      return 'Needs iOS 16.4 or newer'
    case 'on':
      return s.verified ? 'On: public and personal alerts' : 'On: public alerts'
    default:
      return s.blocked ? 'Blocked in the browser settings' : 'Off'
  }
}

function noticeText(s: AlertsState): string | null {
  switch (s.notice) {
    case 'denied':
      return 'Notifications are blocked for this site. Allow them in the browser settings, then try again.'
    case 'dismissed':
      return 'Alerts stay off.'
    case 'not-enabled':
      return 'Alerts are not switched on yet. Check back soon.'
    case 'no-worker':
      return 'Reload the page once, then try again.'
    case 'failed-on':
      return 'Could not turn alerts on. Try again.'
    case 'failed-off':
      return 'Could not turn alerts off. Try again.'
    default:
      return null
  }
}

/**
 * The switch with its words, for Settings and the profile. `session` = the profile's session when the page has one (the
 * device is then linked for personal alerts without a new signature); `onClaim` = the profile's own claim step (otherwise the claim
 * line links to the profile page); `claimed` = the profile exists and only the session on this browser is missing.
 */
export function AlertsCard({
  session,
  onClaim,
  claimBusy,
  claimed,
  onInstall,
  onNavigate,
}: {
  session?: { token: string } | null
  onClaim?: () => void
  claimBusy?: boolean
  claimed?: boolean
  onInstall?: () => void
  onNavigate?: () => void
}) {
  const s = useAlerts()
  const on = s.kind === 'on'
  const actionable = (on || (s.kind === 'off' && !s.blocked)) && !s.busy
  const token = session?.token ?? null
  const titleId = useId()

  // a profile session that exists now: link an already public device once (no signature: the session is the earlier one)
  const linkedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!token || s.kind !== 'on' || s.verified || s.busy || linkedFor.current === token) return
    linkedFor.current = token
    linkAlerts(token)
  }, [token, s.kind, s.verified, s.busy])

  let detail: JSX.Element | null = null
  const notice = noticeText(s)
  if (notice) detail = <Detail role="status">{notice}</Detail>
  else if (s.kind === 'ios-install-first')
    detail = (
      <>
        <Detail>On iPhone and iPad, alerts work only in the installed app (iOS 16.4 or newer). Add CADINU to your Home Screen, open it from there, then turn alerts on.</Detail>
        {onInstall && (
          <Button width="100%" mt="12px" onClick={onInstall} data-testid="pwa-alerts-install-first">
            How to install
          </Button>
        )}
      </>
    )
  else if (s.kind === 'ios-too-old') detail = <Detail>Alerts need iOS 16.4 or newer. Update iOS, then add CADINU to your Home Screen.</Detail>
  else if (s.kind === 'unsupported') detail = <Detail>This browser cannot show alerts. Use Chrome, Edge, Firefox, or Safari on a Mac or in the installed iPhone app.</Detail>
  else if (s.kind === 'off' && s.blocked) detail = <Detail>Notifications are blocked for this site. Allow them in the browser settings, then come back.</Detail>
  else if (on && s.verified && s.verifiedWallet) detail = <Detail>Personal alerts go to {short(s.verifiedWallet)} on this device.</Detail>
  else if (on && !s.verified && token) detail = <Detail role="status">Turning on personal alerts…</Detail>
  else if (on && !s.verified) {
    const line = claimed ? CONFIRM_LINE : CLAIM_LINE
    detail = onClaim ? (
      <ClaimLink as="button" type="button" onClick={onClaim} disabled={claimBusy} data-testid="pwa-alerts-claim">
        {claimBusy ? 'Check your wallet…' : line}
      </ClaimLink>
    ) : (
      <Link href={CLAIM_HREF} passHref legacyBehavior>
        <ClaimLink onClick={onNavigate} data-testid="pwa-alerts-claim">
          {line}
        </ClaimLink>
      </Link>
    )
  } else if (s.kind === 'off') detail = <Detail>Competitions going live and announcements, with no signature. CADINU never sends a transaction or asks for funds in an alert.</Detail>

  return (
    <div data-testid="pwa-alerts-card">
      <Head>
        <div style={{ minWidth: 0 }}>
          <Title id={titleId}>Alerts on this device</Title>
          <Status>{statusLine(s)}</Status>
        </div>
        <SwitchButton
          type="button"
          role="switch"
          aria-checked={on}
          aria-labelledby={titleId}
          aria-busy={Boolean(s.busy)}
          disabled={!actionable}
          onClick={() => toggleAlerts(!on, { token })}
          data-testid="pwa-alerts-switch"
          $on={on}
          $busy={Boolean(s.busy)}
        />
      </Head>
      {detail}
    </div>
  )
}
